/**
 * A comparable fingerprint of an Unlayer design: equal fingerprints mean the same email content (SB-17105).
 *
 * Only the top-level `counters` are left out. Unlayer bumps them when a block is added and never lowers them
 * when it is deleted, so an add-then-delete would otherwise never read as "back to saved". Nothing else is
 * stripped: block ids and `_meta` survive an add-then-delete unchanged, and every field left out is one more
 * way for a real edit to read as no change, which is the failure that matters (an edit dropped without a
 * prompt). Object keys are sorted, because Unlayer does not keep them in one order; array order is content.
 *
 * Anything that is not an object gives `''`.
 */
export function designFingerprint(design: unknown): string {
  if (!design || typeof design !== 'object' || Array.isArray(design)) {
    return '';
  }

  const content = { ...(design as Record<string, unknown>) };
  delete content['counters'];
  return JSON.stringify(canonical(content));
}

/** The value with every object's keys in sorted order, so key order does not change the serialised form. */
function canonical(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonical);
  }
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    return Object.keys(source)
      .sort()
      .reduce<Record<string, unknown>>((sorted, key) => {
        sorted[key] = canonical(source[key]);
        return sorted;
      }, {});
  }
  return value;
}
